import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "@/lib/supabaseClient";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CalendarClock, MapPin, Truck, ChevronRight, ShieldAlert } from "lucide-react";
import { useTenantBranding, TenantLogo } from "@/components/TenantBrand";
import { decomposeInSiteTz, todayInTz } from "@/lib/datetime";

/**
 * Public, token-gated driver dashboard.
 * URL: /driver/:token  (the magic link in driver_assigned emails)
 * No login required — token lives on lbs_driver_profiles.dashboard_token.
 * Lists 60-day window of bookings. Per-booking pages still gated by their
 * own driver_site_info_token, which expires 24h after scheduled_end.
 *
 * Kept separate from the authenticated /driver page (DriverDashboard.jsx)
 * so we never need to log a driver in just to glance at their schedule.
 */
export default function PublicDriverDashboard() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const brand = useTenantBranding({ driver_token: token });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: payload, error } = await supabase.rpc(
          "lbs_get_driver_dashboard",
          { p_token: token }
        );
        if (error) throw error;
        if (!cancelled) setData(payload || {});
      } catch (e) {
        if (!cancelled) setErr(e?.message || "Couldn't load this dashboard.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const visible = useMemo(() => (data?.bookings || []).filter((b) => !CLOSED.has(b.status)), [data]);
  const groups = useMemo(() => groupByWindow(visible), [visible]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500 text-sm">
        Loading your bookings…
      </div>
    );
  }
  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 flex items-start gap-3">
            <ShieldAlert className="h-5 w-5 text-rose-500 shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-slate-900">Can&apos;t open this dashboard</div>
              <div className="text-sm text-slate-500 mt-1">{err}</div>
              <div className="text-xs text-slate-400 mt-3">
                If this link was sent to you in error, ignore it. Otherwise contact
                the site team that issued your booking.
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const d = data?.driver || {};
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <TenantLogo brand={brand} className="mb-4" />
        <div className="mb-6">
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
            {d.first_name ? `${d.first_name}'s` : "Your"} bookings
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Tap any booking to open the site info, map and PPE for that slot.
          </p>
        </div>

        {visible.length === 0 && (
          <Card data-testid="driver-dashboard-empty"><CardContent className="py-10 text-center text-sm text-slate-400">
            No open bookings. New jobs will appear here automatically.
          </CardContent></Card>
        )}

        {["today", "upcoming", "past"].map((k) => (
          groups[k].length > 0 && (
            <section key={k} className="mb-6">
              <h2 className="text-xs uppercase tracking-wider text-slate-500 mb-2">
                {k === "today" ? "Today" : k === "upcoming" ? "Upcoming" : "Past 60 days"}
              </h2>
              <div className="space-y-2">
                {groups[k].map((b) => (
                  <BookingRow key={b.id} b={b} active={b.link_active} />
                ))}
              </div>
            </section>
          )
        ))}

        <p className="text-[11px] text-slate-400 text-center mt-10">
          Bookmark this page — it&apos;s your personal dashboard. timeslot.au
        </p>
      </div>
    </div>
  );
}

function BookingRow({ b, active }) {
  const dt = b.scheduled_start ? new Date(b.scheduled_start) : null;
  const tz = b.site_timezone || "Australia/Sydney";
  const wk = dt && dt.toLocaleDateString("en-AU", { weekday: "short", timeZone: tz });
  const dy = dt && dt.toLocaleDateString("en-AU", { day: "2-digit",   timeZone: tz });
  const mo = dt && dt.toLocaleDateString("en-AU", { month: "short",   timeZone: tz });
  const time = dt && dt.toLocaleTimeString("en-AU", {
    hour: "2-digit", minute: "2-digit", hour12: false, timeZone: tz,
  });
  const inner = (
    <Card className={`hover:border-slate-400 transition ${active ? "" : "opacity-60"}`}>
      <CardContent className="py-3 px-4 flex items-center gap-3">
        <div className="flex flex-col items-center w-14 shrink-0">
          <div className="text-[10px] uppercase tracking-wider text-slate-400">{wk}</div>
          <div className="text-lg font-semibold leading-tight">{dy}</div>
          <div className="text-xs text-slate-500">{mo}</div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold truncate flex items-center gap-2 flex-wrap">
            {b.site_name}
            <Badge variant="secondary" className="capitalize text-[10px] font-normal">
              {b.direction}
            </Badge>
            <Badge variant="secondary" className={`text-[10px] font-normal ${statusClass(b.status)}`}>
              {b.status}
            </Badge>
          </div>
          <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap gap-x-3">
            <span className="flex items-center gap-1"><CalendarClock className="h-3 w-3" />{time}</span>
            {b.bay_name && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{b.bay_name}</span>}
            {b.vehicle_regos?.[0] && <span className="flex items-center gap-1"><Truck className="h-3 w-3" />{b.vehicle_regos.join(", ")}</span>}
          </div>
          <div className="text-[11px] text-slate-400 mt-1">Ref {b.reference_number}</div>
        </div>
        <ChevronRight className="h-4 w-4 text-slate-400" />
      </CardContent>
    </Card>
  );
  if (active && b.driver_site_info_token) {
    return (
      <Link to={`/d/${b.driver_site_info_token}`} data-testid={`booking-row-${b.id}`}>
        {inner}
      </Link>
    );
  }
  return <div data-testid={`booking-row-${b.id}`} title="Booking link expired">{inner}</div>;
}

// Finished or cancelled jobs are hidden from the driver's dashboard.
const CLOSED = new Set(["completed", "cancelled", "rejected", "no_show"]);

function statusClass(s) {
  if (s === "confirmed" || s === "approved" || s === "completed") return "bg-emerald-100 text-emerald-700";
  if (s === "checked_in" || s === "in_progress")                  return "bg-blue-100 text-blue-700";
  if (s === "pending")                                            return "bg-amber-100 text-amber-800";
  if (s === "cancelled")                                          return "bg-rose-100 text-rose-700";
  return "bg-slate-100 text-slate-600";
}

function groupByWindow(rows) {
  const now = Date.now();
  const today = [], upcoming = [], past = [];
  for (const b of rows) {
    const tz = b.site_timezone || "Australia/Sydney";
    const start = b.scheduled_start ? new Date(b.scheduled_start).getTime() : 0;
    if (start && decomposeInSiteTz(b.scheduled_start, tz).dateStr === todayInTz(tz)) today.push(b);
    else if (start > now) upcoming.push(b);
    else past.push(b);
  }
  upcoming.sort((a, b) => new Date(a.scheduled_start) - new Date(b.scheduled_start));
  today.sort((a, b) => new Date(a.scheduled_start) - new Date(b.scheduled_start));
  past.sort((a, b) => new Date(b.scheduled_start) - new Date(a.scheduled_start));
  return { today, upcoming, past };
}
